﻿import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";
import { create } from "lodash";

export const endpointTypesEnum = {
  SFTP: 0,
  FTPS: 1,
  RESTAPI: 2,
  SOAPAPI: 3,
  InternalDBTable: 4,
  VirtualPacking: 5,
  Email: 6,
};

export const endpointTypes = [
  { key: endpointTypesEnum.SFTP, label: "SFTP", isChecked: false },
  { key: endpointTypesEnum.FTPS, label: "FTPS", isChecked: false },
  { key: endpointTypesEnum.RESTAPI, label: "RESTAPI", isChecked: false },
  { key: endpointTypesEnum.SOAPAPI, label: "SOAPAPI", isChecked: false },
  {
    key: endpointTypesEnum.InternalDBTable,
    label: "InternalDBTable",
    isChecked: false,
  },
];

export const appTypesEnum = {
  RetailChannel: 0,
  ERP: 2,
  WMS: 3,
  CMS: 4,
};

export const appTypes = [
  {
    key: appTypesEnum.RetailChannel,
    label: "Retail Channel",
  },
  {
    key: appTypesEnum.ECommerce,
    label: "ECommerce",
  },
  {
    key: appTypesEnum.ERP,
    label: "ERP",
  },
  {
    key: appTypesEnum.WMS,
    label: "WMS",
  },
  {
    key: appTypesEnum.CMS,
    label: "CMS",
  },
];

const standardTypesEnum = {
  X12EDI: 1,
  JSON: 2,
  FlatFile: 3,
  XML: 4,
  CSV: 5,
  ProprietaryDataStream: 6,
};

export const standardTypes = [
  { key: standardTypesEnum.X12EDI, label: "X12EDI", isChecked: false },
  { key: standardTypesEnum.JSON, label: "JSON", isChecked: false },
  { key: standardTypesEnum.FlatFile, label: "FlatFile", isChecked: false },
  { key: standardTypesEnum.XML, label: "XML", isChecked: false },
  {
    key: standardTypesEnum.CSV,
    label: "CSV",
    isChecked: false,
  },
  {
    key: standardTypesEnum.ProprietaryDataStream,
    label: "ProprietaryDataStream",
    isChecked: false,
  },
];

const ConnectionTypesEnum = {
  Customer: 0,
  ERP: 1,
  Warehouse: 2,
};

export const connectionTypes = [
  { key: ConnectionTypesEnum.Customer, label: "Customer", isChecked: false },
  { key: ConnectionTypesEnum.ERP, label: "ERP", isChecked: false },
  { key: ConnectionTypesEnum.Warehouse, label: "Warehouse", isChecked: false },
];

export const FileInStatusEnum = {
  New: 0,
  New_from_UI: 1,
  Retranslating: 5,
  Translated: 10,
  Skipped: 20,
  Invalid: 30,
  Error_at_Re_translating: 40,
  Cancelled: 50,
};

export const PendingArtifactStatusEnum = {
  New: 0,
  ErrorInTranslation: 5,
  AwaitingConfirmation: 20,
  Rejected: 21,
  Confirmed: 22,
  Completed: 30,
};

const defaultNewConnection = {
  model: {
    name: "",
    type: null,
    workflows: [],
    targetConnections: [],
    schemaLibrary: [],
    url: null,
  },
  connectionNames: {
    records: [],
    loaded: true,
    loadingError: "",
  },
  schemaLibrary: {
    records: [],
    loaded: true,
    loadingError: "",
    filters: {
      search: "",
    },
  },
};
const defaultEditConnection = {
  allConnections: [],
  allConnectionsLoaded: true,
  operations: [],
  operationsLoaded: true,
  endpoints: [],
  endpointsLoaded: true,
  selectedPairs: [],
  connectionPairs: [],
  connectionPairsLoaded: true,
  modifiedConnectionPairs: [],
  connectionProfiles: [],
  connectionProfilesLoaded: true,
  connectionPairWorkflow: {},
  model: {},
  open: false,
  id: null,
};

const defaultEditSchema = {
  open: false,
};

export const OperationProcessState = {
  InQueue: 0,
  AllocatingWorker: 10,
  InProgress: 20,
  Paused: 80,
  Cancelled: 90,
  Completed: 100,
  CompletedWithWarning: 101,
  FailedWithError: 110,
  FailedToBeReattempted: 111,
  Hold: 15,
  Archive: 120,
};

export const connEndpointStatus = [
  {
    key: OperationProcessState.Completed,
    label: "Connected",
    isChecked: false,
  },
  {
    key: OperationProcessState.FailedWithError,
    label: "Failed",
    isChecked: false,
  },
];

export const defaultCode = "[DEFAULT]";
export const blankCode = "[BLANK]";

const integrationSlice = createSlice({
  name: "integration",
  initialState: {
    tabSelections: {
      integration: 0,
      connections: 0,
      endpoints: 0,
      newRequestConnection: 0,
      fileManager: 0,
    },
    requestNewConnection: { ...defaultNewConnection },
    editConnection: { ...defaultEditConnection },
    editSchema: { ...defaultEditSchema },
    requestNewConnectionSummary: {
      records: [],
      recordsLoaded: false,
      error: null,
    },
    connectionSummary: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
      filters: {
        status: [],
        type: [],
      },
      triggerRefresh: false,
    },
    endpointSummary: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
      filters: {
        status: [],
        type: [],
      },
    },
    connectionPair: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
    },
    fileManager: {
      records: [],
      recordsLoaded: false,
      LoadedError: null,
      filters: {
        status: [],
      },
    },
    emailManager: {
      records: [],
      recordsLoaded: false,
      LoadedError: null,
      filters: {
        status: [],
      },
    },
    schemaCollectionSummary: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
    },
    connectionSettings: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
    },
    readOptionFields: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
    },
    readItemQualifiers: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
    },
    selectedConnectionInfo: [],
    transactionSettingsDataChangeDetected: false,
    selectedTemplateInfo: [],
    schemaCollectionAdded: [],
    isLoadCollectionClicked: false,
    schemaDocuments: {
      records: [],
      recordsLoaded: false,
      loadingError: null,
    },
    isUpdateSchemaClicked: false,
    selectedDocumentInfo: [],
    editSchemaTabValue: 0,
    mapTabSelected: false,
    standardFilterTypes: [],
    schemaCollectionDocuments: [],
    connectionArtifactDetails: [],
    schemaDocumentAdded: false,

    //shared catalog setup
    catalogSharingSetupDetails: [],
    connectionPairObject: [],
  },

  reducers: {
    setConnectionSummaryFilterTypes: (state, action) => {
      const { value } = action.payload;
      state.connectionSummary.filters.type = value;
    },
    setConnectionSummaryFilterStatus: (state, action) => {
      const { value } = action.payload;
      state.connectionSummary.filters.status = value;
    },

    // request new connection

    setNewConnectionModelURL: (state, action) => {
      state.requestNewConnection.model.url = action.payload;
    },
    setNewConnectionModelName: (state, action) => {
      state.requestNewConnection.model.name = action.payload;
    },
    setNewConnectionModelType: (state, action) => {
      state.requestNewConnection.model.type = action.payload;
    },
    setRequestNewConnectionSchemaConnectionNames: (state, action) => {
      state.requestNewConnection.connectionNames.records = action.payload;
    },
    setRequestNewConnectionSchemaLibraryRecords: (state, action) => {
      state.requestNewConnection.schemaLibrary.records = action.payload;
    },
    setRequestNewConnectionSchemaLibrary: (state, action) => {
      state.requestNewConnection.model.schemaLibrary = action.payload;
    },
    setRequestNewConnectionWorkflows: (state, action) => {
      state.requestNewConnection.model.workflows = action.payload;
    },
    setRequestNewConnectionTargetConnections: (state, action) => {
      state.requestNewConnection.model.targetConnections = action.payload;
    },
    setRequestNewConnectionURL: (state, action) => {
      state.requestNewConnection.model.url = action.payload;
    },
    setEndpointSummaryFilterTypes: (state, action) => {
      const { value } = action.payload;
      state.endpointSummary.filters.type = value;
    },
    setStandardFilterTypes: (state, action) => {
      const { value } = action.payload;
      state.standardFilterTypes = value;
    },
    setEndpointSummaryFilterStatus: (state, action) => {
      const { value } = action.payload;
      state.endpointSummary.filters.status = value;
    },
    setFileManagerFilterStatus: (state, action) => {
      const { value } = action.payload;
      state.fileManager.filters.status = value;

      // heap.io

      if (action.payload && !action.payload.heapIOIsInitValue) {
        const selectedItems = action.payload.value
          .filter((item) => item.isChecked)
          .map((item) => item.Label);

        let filterText;

        if (selectedItems.length === action.payload.value.length) {
          filterText = "All";
        } else if (selectedItems.length === 0) {
          filterText = "None";
        } else {
          filterText = selectedItems.join(",");
        }

        const eventProps = {
          "Filter - Status": filterText,
        };

        window.heap.track("File Manager - Use Filter", eventProps);
      }
    },
    setEmailManagerFilterStatus: (state, action) => {
      const { value } = action.payload;
      state.emailManager.filters.status = value;

      // heap.io

      if (action.payload && !action.payload.heapIOIsInitValue) {
        const selectedItems = action.payload.value
          .filter((item) => item.isChecked)
          .map((item) => item.Label);

        let filterText;

        if (selectedItems.length === action.payload.value.length) {
          filterText = "All";
        } else if (selectedItems.length === 0) {
          filterText = "None";
        } else {
          filterText = selectedItems.join(",");
        }

        const eventProps = {
          "Filter - Status": filterText,
        };

        window.heap.track("Email Manager - Use Filter", eventProps);
      }
    },
    setConnectionRows: (state, action) => {
      const { rows } = action.payload;
      state.connectionRows = rows;
    },

    setEndpointRows: (state, action) => {
      const { rows } = action.payload;
      state.endpointRows = rows;
    },
    setEndpointSummary: (state, action) => {
      const { connectionSummary, endpointSummary } = action.payload;
      state.connectionSummary = connectionSummary;
      state.endpointSummary = endpointSummary;
    },

    setSchemaLibraryFilterSearch: (state, action) => {
      const { value } = action.payload;
      state.requestNewConnection.schemaLibrary.filters.search = value;
    },

    setTabSelectionsIntegration: (state, action) => {
      state.tabSelections.integration = action.payload;
    },
    setTabSelectionsConnections: (state, action) => {
      state.tabSelections.connections = action.payload;
    },
    setTabSelectionsEndpoints: (state, action) => {
      state.tabSelections.endpoints = action.payload;
    },
    setTabSelectionsNewRequestConnection: (state, action) => {
      state.tabSelections.newRequestConnection = action.payload;
    },
    setTabSelectionsFileManager: (state, action) => {
      state.tabSelections.fileManager = action.payload;
    },
    resetNewRequestConnection: (state, action) => {
      state.requestNewConnection = { ...defaultNewConnection };
    },
    setEditConnectionOpen: (state, action) => {
      state.editConnection.open = action.payload;
    },
    setEditConnectionId: (state, action) => {
      const newId = action.payload;
      if (newId !== state.editConnection.id) {
        state.editConnection = { ...defaultEditConnection };
        state.editConnection.id = newId;
      }
    },
    setEditConnectionModel: (state, action) => {
      state.editConnection.model = action.payload;
    },
    setEditConnectionSelectedPairs: (state, action) => {
      state.editConnection.selectedPairs = action.payload;
    },
    setEditConnectionConnectionPairWorkflow: (state, action) => {
      state.editConnection.connectionPairWorkflow = action.payload;
    },
    setEditConnectionModifiedConnectionPairs: (state, action) => {
      state.editConnection.modifiedConnectionPairs = action.payload;
    },
    setEditSchemaOpen: (state, action) => {
      state.editSchema.open = action.payload;
    },
    setSelectedConnection: (state, action) => {
      const { selectedConnectionInfo } = action.payload;
      state.selectedConnectionInfo = selectedConnectionInfo;
    },
    setTransactionSettingsDataChangeDetected: (state, action) => {
      const { transactionSettingsDataChangeDetected } = action.payload;
      state.transactionSettingsDataChangeDetected =
        transactionSettingsDataChangeDetected;
    },
    setSelectedTemplateInfo: (state, action) => {
      const { selectedTemplateInfo } = action.payload;
      state.selectedTemplateInfo = selectedTemplateInfo;
    },
    setSchemaCollectionAdded: (state, action) => {
      const { schemaCollectionAdded } = action.payload;
      state.schemaCollectionAdded = schemaCollectionAdded;
    },
    setIsLoadCollectionClicked: (state, action) => {
      const { isLoadCollectionClicked } = action.payload;
      state.isLoadCollectionClicked = isLoadCollectionClicked;
    },
    setIsUpdateSchemaClicked: (state, action) => {
      const { isUpdateSchemaClicked } = action.payload;
      state.isUpdateSchemaClicked = isUpdateSchemaClicked;
    },
    setSelectedDocumentInfo: (state, action) => {
      const { selectedDocumentInfo } = action.payload;
      state.selectedDocumentInfo = selectedDocumentInfo;
    },
    setEditSchemaTabValue: (state, action) => {
      state.editSchemaTabValue = action.payload;
    },
    setMapTabSelected: (state, action) => {
      state.mapTabSelected = action.payload;
    },
    setTriggerRefreshForConnectionSummary: (state, action) => {
      state.connectionSummary.triggerRefresh =
        !state.connectionSummary.triggerRefresh;
    },
    setSchemaCollectionDocuments: (state, action) => {
      const { schemaCollectionDocuments } = action.payload;
      state.schemaCollectionDocuments = schemaCollectionDocuments;
    },
    setConnectionArtifactDetails: (state, action) => {
      const { connectionArtifactDetails } = action.payload;
      state.connectionArtifactDetails = connectionArtifactDetails;
    },
    setSchemaDocumentAdded: (state, action) => {
      const { schemaDocumentAdded } = action.payload;
      state.schemaDocumentAdded = schemaDocumentAdded;
    },

    //shared catalog setup
    setSharedCatalogSetupDetails: (state, action) => {
      const { catalogSharingSetupDetails } = action.payload;
      state.catalogSharingSetupDetails.push(catalogSharingSetupDetails);
    },
    clearSharedCatalogSetupDetails: (state, action) => {
      const { catalogSharingSetupDetails } = action.payload;
      state.catalogSharingSetupDetails = catalogSharingSetupDetails;
    },
    setConnectionPairObject: (state, action) => {
      const { connectionPairObject } = action.payload;
      state.connectionPairObject.push(connectionPairObject);
    },
    clearConnectionPairObject: (state, action) => {
      const { connectionPairObject } = action.payload;
      state.connectionPairObject = connectionPairObject;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConnectionSummaries.pending, (state, action) => {
        state.connectionSummary.recordsLoaded = false;
      })
      .addCase(fetchConnectionSummaries.rejected, (state, action) => {
        state.connectionSummary.recordsLoaded = true;
        state.connectionSummary.loadingError = action.error.message;
      })
      .addCase(fetchConnectionSummaries.fulfilled, (state, action) => {
        state.connectionSummary.recordsLoaded = true;
        state.connectionSummary.records = action.payload;
      })
      .addCase(fetchEndpointSummaries.rejected, (state, action) => {
        state.endpointSummary.recordsLoaded = true;
        state.endpointSummary.loadingError = action.error.message;
      })
      .addCase(fetchEndpointSummaries.fulfilled, (state, action) => {
        state.endpointSummary.recordsLoaded = true;
        state.endpointSummary.records = action.payload;
      })
      .addCase(fetchSchemaLibraries.pending, (state, action) => {
        state.requestNewConnection.schemaLibrary.loaded = false;
      })
      .addCase(fetchSchemaLibraries.rejected, (state, action) => {
        state.requestNewConnection.schemaLibrary.loaded = true;
        state.requestNewConnection.schemaLibrary.loadingError =
          action.error.message;
      })
      .addCase(fetchSchemaLibraries.fulfilled, (state, action) => {
        state.requestNewConnection.schemaLibrary.loaded = true;
        state.requestNewConnection.schemaLibrary.records = action.payload;
      })
      .addCase(fetchConnectionNames.pending, (state, action) => {
        state.requestNewConnection.connectionNames.loaded = false;
      })
      .addCase(fetchConnectionNames.rejected, (state, action) => {
        state.requestNewConnection.connectionNames.loaded = true;
        state.requestNewConnection.connectionNames.loadingError =
          action.error.message;
      })
      .addCase(fetchConnectionNames.fulfilled, (state, action) => {
        state.requestNewConnection.connectionNames.loaded = true;
        state.requestNewConnection.connectionNames.records = action.payload;
      })

      .addCase(fetchConnectionIds.pending, (state, action) => {
        state.editConnection.allConnectionsLoaded = false;
      })
      .addCase(fetchConnectionIds.rejected, (state, action) => {
        state.editConnection.allConnectionsLoaded = true;
        state.editConnection.loadingError = action.error.message;
      })
      .addCase(fetchConnectionIds.fulfilled, (state, action) => {
        state.editConnection.allConnectionsLoaded = true;
        const parsedRecords = action.payload.map((x) => {
          const json = JSON.parse(x.JSONProperties);
          let typeLabel = "";
          if (json) {
            const label = connectionTypes.filter(
              (x) => x.key == json.ConnectionType,
            )[0];
            if (label) {
              typeLabel = label.label;
            }
          }
          return { ...x, ...json, id: x.ConnectionId, typeLabel: typeLabel };
        });
        state.editConnection.allConnections = parsedRecords;
      })

      .addCase(fetchEndpoints.pending, (state, action) => {
        state.editConnection.endpointsLoaded = false;
      })
      .addCase(fetchEndpoints.rejected, (state, action) => {
        state.editConnection.endpointsLoaded = true;
        state.editConnection.loadingError = action.error.message;
      })
      .addCase(fetchEndpoints.fulfilled, (state, action) => {
        state.editConnection.endpointsLoaded = true;
        state.editConnection.endpoints = action.payload;
      })

      .addCase(fetchConnectionAllPairs.pending, (state, action) => {
        state.editConnection.connectionPairsLoaded = false;
      })
      .addCase(fetchConnectionAllPairs.rejected, (state, action) => {
        state.editConnection.connectionPairsLoaded = true;
        state.editConnection.loadingError = action.error.message;
      })
      .addCase(fetchConnectionAllPairs.fulfilled, (state, action) => {
        state.editConnection.connectionPairsLoaded = true;
        state.editConnection.connectionPairs = action.payload;
      })
      .addCase(fetchNewConnectionRequests.pending, (state, action) => {
        state.requestNewConnectionSummary.recordsLoaded = false;
      })
      .addCase(fetchNewConnectionRequests.rejected, (state, action) => {
        state.requestNewConnectionSummary.recordsLoaded = true;
        state.requestNewConnectionSummary.error = action.error.message;
      })
      .addCase(fetchNewConnectionRequests.fulfilled, (state, action) => {
        state.requestNewConnectionSummary.recordsLoaded = true;
        state.requestNewConnectionSummary.records = action.payload;
      })
      .addCase(fetchConnectionProfiles.pending, (state, action) => {
        state.editConnection.connectionProfilesLoaded = false;
      })
      .addCase(fetchConnectionProfiles.rejected, (state, action) => {
        state.editConnection.connectionProfilesLoaded = true;
        state.editConnection.connectionProfileserror = action.error.message;
      })
      .addCase(fetchConnectionProfiles.fulfilled, (state, action) => {
        state.editConnection.connectionProfilesLoaded = true;
        state.editConnection.connectionProfiles = action.payload;
      })

      .addCase(fetchOperations.pending, (state, action) => {
        state.editConnection.operationsLoaded = false;
      })
      .addCase(fetchOperations.rejected, (state, action) => {
        state.editConnection.operationsLoaded = true;
        state.editConnection.operationserror = action.error.message;
      })
      .addCase(fetchOperations.fulfilled, (state, action) => {
        state.editConnection.operationsLoaded = true;
        state.editConnection.operations = action.payload;
      })
      .addCase(fetchConnectionPairs.pending, (state, action) => {
        state.connectionPair.recordsLoaded = false;
      })
      .addCase(fetchConnectionPairs.rejected, (state, action) => {
        state.connectionPair.recordsLoaded = true;
        state.connectionPair.error = action.error.message;
      })
      .addCase(fetchConnectionPairs.fulfilled, (state, action) => {
        state.connectionPair.recordsLoaded = true;
        state.connectionPair.records = action.payload;
      })
      .addCase(fetchTemplates.pending, (state, action) => {
        state.schemaCollectionSummary.recordsLoaded = false;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.schemaCollectionSummary.recordsLoaded = true;
        state.schemaCollectionSummary.loadingError = action.error.message;
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.schemaCollectionSummary.recordsLoaded = true;
        state.schemaCollectionSummary.records = action.payload;
      })
      .addCase(fetchConnectionSettings.pending, (state, _) => {
        state.connectionSettings.recordsLoaded = false;
      })
      .addCase(fetchConnectionSettings.rejected, (state, action) => {
        state.connectionSettings.recordsLoaded = true;
        state.connectionSettings.loadingError = action.error.message;
      })
      .addCase(fetchConnectionSettings.fulfilled, (state, action) => {
        state.connectionSettings.recordsLoaded = true;
        state.connectionSettings.records = {
          ...action.payload.IsaGsMap,
          ...action.payload.AcknowledgementSettings,
          IsISAGSOptionalFieldRequired:
            action.payload.IsISAGSOptionalFieldRequired,
        };
      })
      .addCase(fetchReadOptionFields.pending, (state, action) => {
        state.readOptionFields.recordsLoaded = false;
      })
      .addCase(fetchReadOptionFields.rejected, (state, action) => {
        state.readOptionFields.recordsLoaded = true;
        state.readOptionFields.loadingError = action.error.message;
      })
      .addCase(fetchReadOptionFields.fulfilled, (state, action) => {
        state.readOptionFields.recordsLoaded = true;
        state.readOptionFields.records = action.payload;
      })
      .addCase(fetchItemQualifiers.pending, (state, action) => {
        state.readItemQualifiers.recordsLoaded = false;
      })
      .addCase(fetchItemQualifiers.rejected, (state, action) => {
        state.readItemQualifiers.recordsLoaded = true;
        state.readItemQualifiers.loadingError = action.error.message;
      })
      .addCase(fetchItemQualifiers.fulfilled, (state, action) => {
        state.readItemQualifiers.recordsLoaded = true;
        state.readItemQualifiers.records = action.payload;
      })
      .addCase(updateSchema.pending, (state, action) => {
        state.schemaDocuments.recordsLoaded = false;
      })
      .addCase(updateSchema.rejected, (state, action) => {
        state.schemaDocuments.recordsLoaded = true;
        state.schemaDocuments.loadingError = action.error.message;
      })
      .addCase(updateSchema.fulfilled, (state, action) => {
        state.schemaDocuments.recordsLoaded = true;
        state.schemaDocuments.records = action.payload;
      });
  },
});

export const fetchOperations = createAsyncThunk(
  "connection-operations",
  async (params) => {
    const response = await axios.get("/connections/operations", {
      params: params,
    });
    return response.data;
  },
);

export const fetchConnectionProfiles = createAsyncThunk(
  "connection-profile-all",
  async (params) => {
    const response = await axios.get("/connections/connection-profile-all", {
      params: params,
    });
    return response.data;
  },
);

export const fetchConnectionAllPairs = createAsyncThunk(
  "connectionpair",
  async (params) => {
    const response = await axios.get("/connectionpair", {
      params: params,
    });
    return response.data;
  },
);

export const fetchConnectionIds = createAsyncThunk(
  "connectionpair/ids",
  async (params) => {
    const response = await axios.get("/connectionpair/ids", {
      params: params,
    });
    return response.data;
  },
);

export const fetchEndpoints = createAsyncThunk("endpoint", async (params) => {
  const response = await axios.get("/endpoint", {
    params: params,
  });
  return response.data;
});

export const fetchConnectionSummaries = createAsyncThunk(
  "connections/connection-summary",
  async (params) => {
    const response = await axios.get("/connections/connection-summary", {
      params: params,
    });
    return response.data;
  },
);

export const fetchSchemaLibraries = createAsyncThunk(
  "connections/schema-library",
  async (params) => {
    const response = await axios.get("/connections/schema-library", {
      params: params,
    });
    return response.data;
  },
);

export const fetchEndpointStatus = createAsyncThunk(
  "endpoint/endpoint-status",
  async (params) => {
    const response = await axios.get("/endpoint/endpoint-status", {
      params: params,
    });
    return response.data;
  },
);

export const fetchConnectionNames = createAsyncThunk(
  "connections/connection-name",
  async (params) => {
    const response = await axios.get("/connections/connection-name", {
      params: params,
    });
    return response.data;
  },
);
export const fetchEndpointSummaries = createAsyncThunk(
  "endpoint/endpoint-manager",
  async (params) => {
    const response = await axios.get("/endpoint/endpoint-manager", {
      params: params,
    });
    return response.data;
  },
);

export const fetchNewConnectionRequests = createAsyncThunk(
  "integrations/fetchNewConnectionRequests",
  async (_, { dispatch }) => {
    const response = await axios.get("/connections/new-connection-request");
    const rowData = response.data?.map((element, index) => {
      element.id = index;
      return element;
    });
    return rowData;
  },
);

export const fetchConnectionPairs = createAsyncThunk(
  "integrations/fetchConnectionPairs",
  async (params) => {
    const response = await axios.get("/connectionpair/connection-pairs", {
      params: params,
    });
    return response.data;
  },
);

export const fetchTemplates = createAsyncThunk(
  "integration/fetchTemplates",
  async ({ filter }) => {
    const response = await axios.get("/template", {
      params: { filter: filter },
    });
    return response.data;
  },
);

export const fetchConnectionSettings = createAsyncThunk(
  "integration/fetchConnectionSettings",
  async ({ connectionId, artifactType, connectionArtifactSchemaId }) => {
    const response = await axios.get(
      "/connectionartifactschema/connection-settings",
      {
        params: {
          connectionId: connectionId,
          artifactType: artifactType,
          connectionArtifactSchemaId: connectionArtifactSchemaId,
        },
      },
    );
    return response.data;
  },
);

export const fetchReadOptionFields = createAsyncThunk(
  "integration/fetchReadOptionFields",
  async ({ connectionId, artifactType }) => {
    const response = await axios.get(
      "/connectionartifactschema/readoption-fields",
      {
        params: { connectionId: connectionId, artifactType: artifactType },
      },
    );
    return response.data.values;
  },
);

export const fetchItemQualifiers = createAsyncThunk(
  "integration/fetchItemQualifiers",
  async (params) => {
    const response = await axios.get(
      "/connectionartifactschema/ItemQualifiers",
      {
        params: { connectionId: params },
      },
    );
    return response.data;
  },
);

export const fetchFiles = createAsyncThunk(
  "file/inbound-files",
  async (params) => {
    const response = await axios.get("/file/inbound-files", {
      params: params,
    });

    return response.data;
  },
);

export const loadSchemaCollection = createAsyncThunk(
  "integration/loadSchemaCollection",
  async ({ templateId, connectionId }) => {
    const response = await axios.post(
      "/template/load-schema-collection",
      null,
      {
        params: {
          templateId: templateId,
          connectionId: connectionId,
        },
      },
    );
    return response;
  },
);

export const updateSchema = createAsyncThunk(
  "integration/updateSchema",
  async ({ schemaCollectionId, connectionId }) => {
    const response = await axios.get("/template/endpoint-schema", {
      params: {
        schemaCollectionId: schemaCollectionId,
        connectionId: connectionId,
      },
    });
    return response.data;
  },
);

export const saveSchema = createAsyncThunk(
  "integration/saveSchema",
  async (params) => {
    const { endpointSchemaId, connectionId, artifactType } = params;
    const response = await axios.post("/template/endpoint-schema", null, {
      params: {
        endpointSchemaId: endpointSchemaId,
        connectionId: connectionId,
        artifactType: artifactType,
      },
    });
    return response;
  },
);

export const removeSchemaCollection = createAsyncThunk(
  "integration/removeSchemaCollection",
  async ({ connectionId, schemaCollectionId }) => {
    const response = await axios.put(
      "/template/remove-schema-collection",
      null,
      {
        params: {
          connectionId: connectionId,
          schemaCollectionId: schemaCollectionId,
        },
      },
    );
    return response;
  },
);

export const updateSchemaCollection = createAsyncThunk(
  "integration/updateSchemaCollection",
  async ({ connectionId, schemaCollectionId }) => {
    const response = await axios.put(
      "/template/update-schema-collection",
      null,
      {
        params: {
          connectionId: connectionId,
          schemaCollectionId: schemaCollectionId,
        },
      },
    );
    return response;
  },
);

export const selectAllConnectionSummaries = (state) =>
  state.tempInfo.integration.connectionSummary.records;
export const selectConnectionSummaryFilterTypes = (state) =>
  state.tempInfo.integration.connectionSummary.filters.type;
export const selectConnectionSummaryFilterStatus = (state) =>
  state.tempInfo.integration.connectionSummary.filters.status;
export const selectConnectionSummaryRecordLoaded = (state) =>
  state.tempInfo.integration.connectionSummary.recordsLoaded;
export const selectConnectionSummaryRecords = (state) =>
  state.tempInfo.integration.connectionSummary.records;
export const selectNewConnectionModelTargetConnections = (state) =>
  state.tempInfo.integration.requestNewConnection.model.targetConnections;
export const selectConnectionNamesRecords = (state) =>
  state.tempInfo.integration.requestNewConnection.connectionNames.records;
export const selectConnectionNamesLoaded = (state) =>
  state.tempInfo.integration.requestNewConnection.connectionNames.loaded;
export const selectNewConnectionModelWorkflows = (state) =>
  state.tempInfo.integration.requestNewConnection.model.workflows;
export const selectNewConnectionModelSchemaLibrary = (state) =>
  state.tempInfo.integration.requestNewConnection.model.schemaLibrary;
export const selectSchemaLibraryRecords = (state) =>
  state.tempInfo.integration.requestNewConnection.schemaLibrary.records;
export const selectSchemaLibraryLoaded = (state) =>
  state.tempInfo.integration.requestNewConnection.schemaLibrary.loaded;
export const selectSchemaLibraryFilterSearch = (state) =>
  state.tempInfo.integration.requestNewConnection.schemaLibrary.filters.search;
export const selectEndpointSummaryFilterTypes = (state) =>
  state.tempInfo.integration.endpointSummary.filters.type;
export const selectEndpointSummaryFilterStatus = (state) =>
  state.tempInfo.integration.endpointSummary.filters.status;
export const selectEndpointSummaryRecordLoaded = (state) =>
  state.tempInfo.integration.endpointSummary.recordsLoaded;
export const selectEndpointSummaryRecords = (state) =>
  state.tempInfo.integration.endpointSummary.records;
export const selectNewConnectionModelName = (state) =>
  state.tempInfo.integration.requestNewConnection.model.name;
export const selectNewConnectionModelType = (state) =>
  state.tempInfo.integration.requestNewConnection.model.type;
export const selectNewConnectionModelURL = (state) =>
  state.tempInfo.integration.requestNewConnection.model.url;
export const selectNewConnectionModel = (state) =>
  state.tempInfo.integration.requestNewConnection.model;
export const selectTabSelectionsIntegration = (state) =>
  state.tempInfo.integration.tabSelections.integration;
export const selectTabSelectionsConnections = (state) =>
  state.tempInfo.integration.tabSelections.connections;
export const selectTabSelectionsEndpoints = (state) =>
  state.tempInfo.integration.tabSelections.endpoints;
export const selectTabSelectionsNewRequestConnection = (state) =>
  state.tempInfo.integration.tabSelections.newRequestConnection;
export const selectTabselectionsFileManager = (state) =>
  state.tempInfo.integration.tabSelections.fileManager;
export const selectConnectionModel = (state) =>
  state.tempInfo.integration.editConnection.model;
export const selectEditConnectionOpen = (state) =>
  state.tempInfo.integration.editConnection.open;
export const selectEditConnectionId = (state) =>
  state.tempInfo.integration.editConnection.id;
export const selectEditConnectionAllConnections = (state) =>
  state.tempInfo.integration.editConnection.allConnections;
export const selectEditConnectionEndpoints = (state) =>
  state.tempInfo.integration.editConnection.endpoints;
export const selectEditConnectionEndpointsLoaded = (state) =>
  state.tempInfo.integration.editConnection.endpointsLoaded;
export const selectEditConnectionConnectionPairs = (state) =>
  state.tempInfo.integration.editConnection.connectionPairs;
export const selectEditConnectionConnectionPairsLoaded = (state) =>
  state.tempInfo.integration.editConnection.connectionPairsLoaded;
export const selectEditConnectionconnectionProfilesLoaded = (state) =>
  state.tempInfo.integration.editConnection.connectionProfilesLoaded;
export const selectEditConnectionConnectionProfiles = (state) =>
  state.tempInfo.integration.editConnection.connectionProfiles;
export const selectEditConnectionOperations = (state) =>
  state.tempInfo.integration.editConnection.operations;
export const selectEditConnectionConnectionPairWorkflow = (state) =>
  state.tempInfo.integration.editConnection.connectionPairWorkflow;
export const selectEditConnectionModifiedConnectionPairs = (state) =>
  state.tempInfo.integration.editConnection.modifiedConnectionPairs;
export const selectEditConnectionSelectedPairs = (state) =>
  state.tempInfo.integration.editConnection.selectedPairs;
export const selectFileManagerFilterStatus = (state) =>
  state.tempInfo.integration.fileManager.filters.status;
export const selectFileManagerFilesLoaded = (state) =>
  state.tempInfo.integration.fileManager.recordsLoaded;
export const selectFileManagerRecords = (state) =>
  state.tempInfo.integration.fileManager.records;
export const selectEmailManagerFilterStatus = (state) =>
  state.tempInfo.integration.emailManager.filters.status;
export const selectEditSchemaOpen = (state) =>
  state.tempInfo.integration.editSchema.open;
export const selectSchemaCollectionRecords = (state) =>
  state.tempInfo.integration.schemaCollectionSummary.records;
export const selectConnectionSettings = (state) =>
  state.tempInfo.integration.connectionSettings.records;
export const selectReadOptionFields = (state) =>
  state.tempInfo.integration.readOptionFields.records;
export const selectReadItemQualifiers = (state) =>
  state.tempInfo.integration.readItemQualifiers.records;
export const selectSelectedConnection = (state) =>
  state.tempInfo.integration.selectedConnectionInfo;
export const selectTransactionSettingsDataChangeDetected = (state) =>
  state.tempInfo.integration.transactionSettingsDataChangeDetected;
export const selectSchemaCollectionAdded = (state) =>
  state.tempInfo.integration.schemaCollectionAdded;
export const selectIsLoadCollectionClicked = (state) =>
  state.tempInfo.integration.isLoadCollectionClicked;
export const selectSchemaDocuments = (state) =>
  state.tempInfo.integration.schemaDocuments.records;
export const selectUpdateSchemaClicked = (state) =>
  state.tempInfo.integration.isUpdateSchemaClicked;
export const selectSelectedTemplateInfo = (state) =>
  state.tempInfo.integration.selectedTemplateInfo;
export const selectSelectedDocumentInfo = (state) =>
  state.tempInfo.integration.selectedDocumentInfo;

export const selectEditSchemaTabValue = (state) =>
  state.tempInfo.integration.editSchemaTabValue;
export const selectMapTabSelected = (state) =>
  state.tempInfo.integration.mapTabSelected;
export const selectStandardFilterTypes = (state) =>
  state.tempInfo.integration.standardFilterTypes;
export const selectSchemaCollectionInfo = (state) =>
  state.tempInfo.integration.schemaCollectionDocuments;
export const selectConnectionArtifactDetails = (state) =>
  state.tempInfo.integration.connectionArtifactDetails;
export const selectSchemaDocumentAdded = (state) =>
  state.tempInfo.integration.schemaDocumentAdded;

export const selectRequestedNewConnections = (state) =>
  state.tempInfo.integration.requestNewConnectionSummary.records;
export const selectRequestedNewConnectionsSummaryRecordLoaded = (state) =>
  state.tempInfo.integration.requestNewConnectionSummary.recordsLoaded;

export const selectConnectionPairs = (state) =>
  state.tempInfo.integration.connectionPair.records;
export const selectConnectionPairsRecordLoaded = (state) =>
  state.tempInfo.integration.connectionPair.recordsLoaded;
export const selectTriggerRefreshForConnectionSummary = (state) =>
  state.tempInfo.integration.connectionSummary.triggerRefresh;

//shared catalog setup
export const selectCatalogSharingSetupDetails = (state) =>
  state.tempInfo.integration.catalogSharingSetupDetails;
export const selectConnectionPairObject = (state) =>
  state.tempInfo.integration.connectionPairObject;

export const {
  setConnectionSummaryFilterTypes,
  setConnectionSummaryFilterStatus,
  setSchemaLibraryFilterSearch,
  setRequestNewConnectionName,
  setRequestNewConnectionType,
  setRequestNewConnectionSchemaLibrary,
  setRequestNewConnectionTargetConnections,
  setRequestNewConnectionURL,
  setEndpointSummaryFilterTypes,
  setEndpointSummaryFilterStatus,
  setFileManagerFilterStatus,
  setEmailManagerFilterStatus,
  setConnectionRows,
  setEndpointRows,
  setEndpointSummary,
  setRequestNewConnectionSchemaLibraryRecords,
  setRequestNewConnectionWorkflows,
  setRequestNewConnectionSchemaConnectionNames,
  setNewConnectionModelName,
  setNewConnectionModelType,
  setNewConnectionModelURL,
  setTabSelectionsIntegration,
  setTabSelectionsConnections,
  setTabSelectionsEndpoints,
  setTabSelectionsNewRequestConnection,
  setTabSelectionsFileManager,
  resetNewRequestConnection,
  setEditConnectionOpen,
  setEditConnectionId,
  setEditConnectionModel,
  setEditSchemaOpen,
  setSelectedConnection,
  setTransactionSettingsDataChangeDetected,
  setSelectedTemplateInfo,
  setSchemaCollectionAdded,
  setIsLoadCollectionClicked,
  setIsUpdateSchemaClicked,
  setSelectedDocumentInfo,
  setEditSchemaTabValue,
  setMapTabSelected,
  setStandardFilterTypes,
  setEditConnectionSelectedPairs,
  setEditConnectionConnectionPairWorkflow,
  setEditConnectionModifiedConnectionPairs,
  setSchemaCollectionDocuments,
  setTriggerRefreshForConnectionSummary,
  setConnectionArtifactDetails,
  setSchemaDocumentAdded,
  setSharedCatalogSetupDetails,
  clearSharedCatalogSetupDetails,
  setConnectionPairObject,
  clearConnectionPairObject,
} = integrationSlice.actions;
export default integrationSlice.reducer;
